/**
 * DevDialogueBox — a minimal, clearly-labelled development dialogue panel implementing IDialogueBox.
 * Used only when the UI builder's real box is not present on UIScene (see resolveDialogueBox).
 * Live Chinese text (never baked), typewriter driven by the owning scene's update loop.
 */
import Phaser from 'phaser';
import type { IDialogueBox } from '@/core/types/contracts';
import { Depth, FONT_BODY, GAME_H, GAME_W } from '@/core/config';
import { SceneKeys } from '@/core/scenes/keys';
import { getDialogueBox } from '@/ui';
import { ACTOR_DEFS } from '@/characters';

/** Live-text font for dev dialogue/HUD text (bundled "Nantian Sans" with system CJK fallbacks). */
export const DIALOGUE_FONT = FONT_BODY;

export interface DevDialogueBoxOptions {
  /** Characters per second while typing (default 30). */
  charsPerSec?: () => number;
  displayName?: (speakerId: string) => string;
  depth?: number;
}

export class DevDialogueBox implements IDialogueBox {
  destroyed = false;
  private readonly container: Phaser.GameObjects.Container;
  private readonly nameText: Phaser.GameObjects.Text;
  private readonly bodyText: Phaser.GameObjects.Text;
  private readonly hint: Phaser.GameObjects.Text;
  private readonly choiceTexts: Phaser.GameObjects.Text[] = [];
  private fullText = '';
  private shownChars = 0;
  private acc = 0;
  private typing = false;
  private resolveShow: (() => void) | null = null;
  private choiceResolve: ((i: number) => void) | null = null;
  private choiceIndex = 0;
  private readonly history: { speaker?: string; text: string }[] = [];
  private readonly cps: () => number;
  private readonly displayName: (id: string) => string;
  private readonly keyHandlers: { ev: string; fn: () => void }[] = [];

  constructor(private readonly scene: Phaser.Scene, opts: DevDialogueBoxOptions = {}) {
    this.cps = opts.charsPerSec ?? (() => 30);
    this.displayName = opts.displayName ?? ((id) => ACTOR_DEFS[id]?.displayName ?? id);
    const depth = opts.depth ?? Depth.UI + 100;
    const h = 110, y = GAME_H - h - 6, x = 6, w = GAME_W - 12;
    const panel = scene.add.rectangle(0, 0, w, h, 0x120e0a, 0.92).setOrigin(0).setStrokeStyle(2, 0x8c2a22);
    const tag = scene.add.text(w - 6, 4, 'dev dialogue box', { fontFamily: DIALOGUE_FONT, fontSize: '9px', color: '#6a5a44' }).setOrigin(1, 0);
    this.nameText = scene.add.text(12, 8, '', { fontFamily: DIALOGUE_FONT, fontSize: '14px', color: '#e2c48c', fontStyle: 'bold' });
    this.bodyText = scene.add.text(12, 30, '', { fontFamily: DIALOGUE_FONT, fontSize: '14px', color: '#f3e6c8', wordWrap: { width: w - 24 }, lineSpacing: 4 });
    this.hint = scene.add.text(w - 12, h - 14, '▼', { fontFamily: DIALOGUE_FONT, fontSize: '12px', color: '#e2c48c' }).setOrigin(1, 0.5).setVisible(false);
    this.container = scene.add.container(x, y, [panel, tag, this.nameText, this.bodyText, this.hint]).setDepth(depth).setVisible(false);
    this.container.setScrollFactor(0);
    scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
  }

  show(line: { speaker?: string; portrait?: string; emotion?: string; text: string }, opts: { instant: boolean }): Promise<void> {
    this.clearChoices();
    this.container.setVisible(true);
    this.nameText.setText(line.speaker ? this.displayName(line.speaker) : '');
    this.bodyText.setY(line.speaker ? 30 : 12);
    this.fullText = line.text;
    this.shownChars = 0;
    this.acc = 0;
    this.hint.setVisible(false);
    if (opts.instant || this.fullText.length === 0) {
      this.typing = false;
      this.bodyText.setText(this.fullText);
      this.hint.setVisible(true);
      return Promise.resolve();
    }
    this.bodyText.setText('');
    this.typing = true;
    return new Promise<void>((resolve) => { this.resolveShow = resolve; });
  }

  completeTyping(): void {
    if (!this.typing) return;
    this.shownChars = this.fullText.length;
    this.finishTyping();
  }

  isTyping(): boolean { return this.typing; }

  hide(): Promise<void> {
    this.clearChoices();
    this.typing = false;
    this.container.setVisible(false);
    return Promise.resolve();
  }

  showChoices(choices: { text: string }[]): Promise<number> {
    this.clearChoices();
    if (choices.length === 0) return Promise.resolve(0);
    this.hint.setVisible(false);
    const baseY = 30 + this.bodyText.height + 6;
    choices.forEach((c, i) => {
      const t = this.scene.add.text(24, baseY + i * 18, `  ${c.text}`, { fontFamily: DIALOGUE_FONT, fontSize: '13px', color: '#c9b89a' }).setInteractive({ useHandCursor: true });
      t.on('pointerover', () => this.selectChoice(i));
      t.on('pointerdown', () => { this.selectChoice(i); this.confirmChoice(); });
      this.container.add(t);
      this.choiceTexts.push(t);
    });
    this.choiceIndex = 0;
    this.selectChoice(0);
    const kb = this.scene.input.keyboard;
    const bind = (ev: string, fn: () => void) => { kb?.on(ev, fn); this.keyHandlers.push({ ev, fn }); };
    bind('keydown-UP', () => this.selectChoice(this.choiceIndex - 1));
    bind('keydown-DOWN', () => this.selectChoice(this.choiceIndex + 1));
    bind('keydown-W', () => this.selectChoice(this.choiceIndex - 1));
    bind('keydown-S', () => this.selectChoice(this.choiceIndex + 1));
    bind('keydown-ENTER', () => this.confirmChoice());
    bind('keydown-SPACE', () => this.confirmChoice());
    bind('keydown-Z', () => this.confirmChoice());
    return new Promise<number>((resolve) => { this.choiceResolve = resolve; });
  }

  pushHistory(speaker: string | undefined, text: string): void {
    this.history.push({ speaker, text });
    if (this.history.length > 100) this.history.shift();
  }
  getHistory(): readonly { speaker?: string; text: string }[] { return this.history; }
  get visible(): boolean { return this.container.visible; }
  isVisible(): boolean { return this.container.visible; }
  currentText(): string { return this.bodyText.text; }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
    this.scene.events.off(Phaser.Scenes.Events.SHUTDOWN, this.destroy, this);
    this.clearChoices();
    const r = this.resolveShow; this.resolveShow = null; r?.();
    this.container.destroy(true);
  }

  private update(_time: number, delta: number): void {
    if (!this.typing) return;
    this.acc += (delta / 1000) * Math.max(1, this.cps());
    const n = Math.floor(this.acc);
    if (n <= 0) return;
    this.acc -= n;
    this.shownChars = Math.min(this.fullText.length, this.shownChars + n);
    this.bodyText.setText(this.fullText.slice(0, this.shownChars));
    if (this.shownChars >= this.fullText.length) this.finishTyping();
  }

  private finishTyping(): void {
    this.typing = false;
    this.bodyText.setText(this.fullText);
    this.hint.setVisible(true);
    const r = this.resolveShow;
    this.resolveShow = null;
    r?.();
  }

  private selectChoice(i: number): void {
    if (this.choiceTexts.length === 0) return;
    this.choiceIndex = (i + this.choiceTexts.length) % this.choiceTexts.length;
    this.choiceTexts.forEach((t, k) => {
      const label = t.text.replace(/^[▶ ] /, '');
      t.setText(`${k === this.choiceIndex ? '▶' : ' '} ${label}`).setColor(k === this.choiceIndex ? '#ffb347' : '#c9b89a');
    });
  }

  private confirmChoice(): void {
    const r = this.choiceResolve;
    if (!r) return;
    const idx = this.choiceIndex;
    this.clearChoices();
    r(idx);
  }

  private clearChoices(): void {
    const kb = this.scene.input.keyboard;
    for (const h of this.keyHandlers) kb?.off(h.ev, h.fn);
    this.keyHandlers.length = 0;
    for (const t of this.choiceTexts) t.destroy();
    this.choiceTexts.length = 0;
    this.choiceResolve = null;
  }
}

const devBoxes = new WeakMap<Phaser.Scene, DevDialogueBox>();

/**
 * The real dialogue box from UIScene when the UI builder has installed one, otherwise a per-scene
 * DevDialogueBox (created lazily, destroyed with the scene).
 */
export function resolveDialogueBox(scene: Phaser.Scene, opts?: DevDialogueBoxOptions): IDialogueBox {
  const ui = scene.scene.get(SceneKeys.UI) as (Phaser.Scene & { dialogueBox?: IDialogueBox }) | null;
  if (ui?.dialogueBox) return getDialogueBox(scene);
  let dev = devBoxes.get(scene);
  if (!dev || dev.destroyed) { dev = new DevDialogueBox(scene, opts); devBoxes.set(scene, dev); }
  return dev;
}
